import ArcGISTiledMapServiceLayer from 'esri/layers/ArcGISTiledMapServiceLayer';
import ArcGISDynamicMapServiceLayer from 'esri/layers/ArcGISDynamicMapServiceLayer';
import FeatureLayer from 'esri/layers/FeatureLayer';
import EsriMap from 'esri/map';
import Search from 'esri/dijit/Search';
import IdentifyTask from 'esri/tasks/IdentifyTask';
import IdentifyParameters from 'esri/tasks/IdentifyParameters';
import { startLegendObserver } from 'utils/LegendUtil';
import ImageServiceIdentifyTask from 'esri/tasks/ImageServiceIdentifyTask';
import ImageServiceIdentifyParameters from 'esri/tasks/ImageServiceIdentifyParameters';
import webMercatorUtils from 'esri/geometry/webMercatorUtils';
import Point from 'esri/geometry/Point';
import SpatialReference from 'esri/SpatialReference';
import Legend from 'esri/dijit/Legend';
import esriRequest from 'esri/request';
import PictureMarkerSymbol from 'esri/symbols/PictureMarkerSymbol';
import Graphic from 'esri/graphic';

import esriBundle from 'dojo/i18n!esri/nls/jsapi';

import { mapActions } from 'actions/MapActions';
import { criteriaActions } from 'actions/CriteriaActions';
import { criteriaStore } from 'stores/CriteriaStore';
import { leftPanelStore } from 'stores/LeftPanelStore';
import { headerActions } from 'actions/HeaderActions';
import { reportStore } from 'stores/ReportStore';

import alt from 'js/alt';
import { loads } from 'js/config/config';
import { mapConfig } from 'js/config/config';
import { elevationReference, mhwReference } from 'js/config/config';
import { ESRI_TOPO_BASEMAP_TITLE, USA_TOPO_BASEMAP_TITLE } from 'js/config/basemaps.config';
import _ from 'lodash';

class MapStore {
  constructor() {
    esriBundle.widgets.legend.NLS_noLegend = 'Select data to display';

    this.map = {};
    this.selectionState = 'Address';
    this.showBasemapGallery = false;
    this.showMeasureTool = false;
    this.showShareLink = false;
    this.legend = {};
    this.showLegend = false;
    this.basemapGallery = {};
    this.link = '';
    this.showLoginOptions = false;
    this.showLockedModal = false;
    this.isHawaii = false;
    this.has722WinterWindContourLegend = false;

    this.bindListeners({
      updateZoom: mapActions.updateZoom,
      updateVisibility: mapActions.updateLayerVisibility,
      createMap: mapActions.createMap,
      reverseGeocoder: mapActions.reverseGeocoder,
      searchAddress: mapActions.searchAddress,
      selectionStateChange: criteriaActions.toggleLocationDiv,
      toggleBasemapGallery: headerActions.toggleBasemapGallery,
      hideBasemapGallery: [headerActions.hideBasemapGallery, mapActions.closeBasemap],
      toogleMeasureTool: headerActions.toogleMeasureTool,
      hideMeasureTool: [headerActions.hideMeasureTool, mapActions.closeMeasure],
      toggleShareLink: headerActions.toggleShareLink,
      hideShareLink: headerActions.hideShareLink,
      addLegend: mapActions.mapContainerLoaded,
      updateLegend: mapActions.updateLegend,
      hideLegend: mapActions.hideLegend,
      toggleLegend: mapActions.toggleLegend,
      showLegendFunction: mapActions.showLegend,
      saveBasemapGallery: mapActions.saveBasemapGallery,
      login: headerActions.login,
      goToSite: mapActions.goToSite,
      setHash: mapActions.setHash,
      toggleLoginOptions: mapActions.toggleLoginOptions,
      setLockedModalVisibility: mapActions.setLockedModalVisibility,
    });
  }

  setHash(hash) {
    this.link = hash;
  }

  setLockedModalVisibility(bool) {
    this.showLockedModal = bool;
  }

  toggleLoginOptions() {
    this.showLoginOptions = !this.showLoginOptions;
  }

  login() {
    //debugger;
  }

  handleUSATopoBasemap() {
    if (this.basemapGallery.getSelected().title === USA_TOPO_BASEMAP_TITLE && this.map.getZoom() >= 16) {
      const topographicBasemap = this.basemapGallery.basemaps.find(
        (basemap) => basemap.title === ESRI_TOPO_BASEMAP_TITLE
      );

      if (topographicBasemap) {
        this.basemapGallery.select(topographicBasemap.id);
      }
    }
  }

  saveBasemapGallery(bmg) {
    this.basemapGallery = bmg;

    this.basemapGallery.on('selection-change', () => {
      this.handleUSATopoBasemap();
    });
  }

  showLegendFunction() {
    this.showLegend = true;
  }

  toggleBasemapGallery() {
    this.showBasemapGallery = !this.showBasemapGallery;
  }

  hideBasemapGallery() {
    this.showBasemapGallery = false;
  }

  toogleMeasureTool() {
    this.showMeasureTool = !this.showMeasureTool;
  }

  hideMeasureTool() {
    this.showMeasureTool = false;
  }

  toggleShareLink() {
    this.showShareLink = !this.showShareLink;
  }

  hideShareLink() {
    this.showShareLink = false;
  }

  toggleLegend() {
    this.showLegend = !this.showLegend;
  }

  createMap(initSettings) {
    this.map = new EsriMap('map', mapConfig.options);
    this.map.on('load', () => {
      // handling zoom intial hash
      if (initSettings.hasOwnProperty('zoom') && typeof initSettings.zoom === 'string') {
        this.map.setZoom(Number(initSettings.zoom));
      }

      if (
        initSettings.hasOwnProperty('latitude') &&
        initSettings.hasOwnProperty('longitude') &&
        typeof initSettings.latitude === 'string' &&
        typeof initSettings.longitude === 'string'
      ) {
        this.map.centerAt(new Point(Number(initSettings.longitude), Number(initSettings.latitude)));
        this.reverseGeocoder({ lat: initSettings.latitude, long: initSettings.longitude });
      }

      // add load layers
      this.addLoadLayers();

      // Only after load layers have been added - determine if any overlays need to be turned in via hash
      if (
        initSettings.hasOwnProperty('layerVisible') &&
        typeof initSettings.layerVisible === 'string' &&
        initSettings.layerVisible !== ''
      ) {
        mapActions.updateLayerVisibility({ id: initSettings.layerVisible, checked: true });
      }
    });

    // This is the function that is called whenever the user clicks on the map
    this.map.on('click', (evt) => {
      if (!leftPanelStore.getState().viewReport && this.selectionState === 'mapclick' && !this.showMeasureTool) {
        this.mapClickLocation(evt.mapPoint);
      }
    });

    this.map.on('update-end', () => {
      // Grab loads from reportStore
      let loads = reportStore.getState().loads;
      // Loop through loads
      loads.forEach((load) => {
        // If the load is visible on the map
        if (load.visible) {
          //Init variable
          let visibleAtMapScale = false;
          // Special Case for Flood Load
          if (load.label === 'Flood') {
            visibleAtMapScale = this.map.getZoom() <= 13 ? false : true;
          } else {
            // Loop through each Load's layers
            load.layers.map.forEach((layer) => {
              visibleAtMapScale = this.map.getLayer(layer.id).visibleAtMapScale;
            });
          }

          // If the layer is not visible at the currently scale/zoom
          if (visibleAtMapScale === false) {
            // Turn off the load's visibility state
            load.visible = false;
            // Update the layer's visibility
            this.updateVisibility({ id: load.label, checked: false });
          }
        }
      });
      // Emit change in report store
      reportStore.emitChange();
      // Update legend
      this.updateLegend();
    });

    this.map.on('zoom-end', () => {
      if (this.basemapGallery.getSelected()) {
        this.handleUSATopoBasemap();
      }
    });

    let geocoderOptions = {
      map: this.map,
      enableHighlight: true,
      enableInfoWindow: false,
      locationToAddressDistance: 30,
    };

    this.search = Search(geocoderOptions, 'geocoder');
    this.search.on('focus', () => {
      this.map.graphics.clear();
    });
    this.search.on('select-result', (args) => {
      let addressPieces = args.result.name.split(',');
      let response = {};

      response.address = {
        Address: typeof addressPieces[0] === 'undefined' ? '' : addressPieces[0].trim(),
        City: typeof addressPieces[1] === 'undefined' ? '' : addressPieces[1].trim(),
        Region: typeof addressPieces[2] === 'undefined' ? '' : addressPieces[2].trim(),
        Postal: typeof addressPieces[3] === 'undefined' ? '' : addressPieces[3].trim(),
      };
      response.location = args.result.feature.geometry;

      mapActions.searchSelectResult(response);
      this.search.set('value', args.result.name);
      this.queryElevation(args.result.feature.geometry);
      this.queryElevationReferenceDatum(args.result.feature.geometry);
    });

    window.map = this.map;
  }

  addLoadLayers() {
    let loadsWithLayer = _.filter(loads, (load) => 'layers' in load);
    let layerObjs = [];
    for (let load of loadsWithLayer) {
      for (let layer of load.layers.map) {
        layerObjs.push(this.createLayer(layer));
      }
    }
    // Adding layers to the map
    this.map.addLayers(layerObjs);
  }

  /**
   * Creates layer depending on their layer type
   * @param  {[type]} buildLayer [description]
   * @return {[type]}            [description]
   */
  createLayer(buildLayer) {
    let layer;
    switch (buildLayer.type) {
      case 'tile':
        layer = new ArcGISTiledMapServiceLayer(buildLayer.url, {
          visible: buildLayer.visible,
          id: buildLayer.id,
          opacity: buildLayer.opacity || 0.8,
        });
        break;
      case 'feature':
        layer = new FeatureLayer(buildLayer.url, {
          visible: buildLayer.visible,
          id: buildLayer.id,
          opacity: buildLayer.opacity || 0.8,
        });
        break;
      case 'dynamic':
        layer = new ArcGISDynamicMapServiceLayer(buildLayer.url, {
          visible: buildLayer.visible,
          id: buildLayer.id,
          opacity: buildLayer.opacity || 0.8,
        });
        layer.setImageFormat('png32');
        break;
      default:
        break;
    }

    // This if statement will become true if the layer is dynamic
    if (typeof layer.setVisibleLayers === 'function') {
      layer.setVisibleLayers(buildLayer.visibleLayers);
    }
    return layer;
  }

  addLegend() {
    const LEGEND_ELEMENT_ID = 'legend';
    let legend = new Legend(
      {
        map: this.map,
        autoUpdate: false,
        layerInfos: [],
      },
      LEGEND_ELEMENT_ID
    );
    legend.startup();

    this.legend = legend;
    const legendElement = document.getElementById(LEGEND_ELEMENT_ID);

    if (legendElement) {
      startLegendObserver(legendElement);
    }
  }

  updateZoom(updateObj) {
    if (!updateObj.stopZoom) {
      if (updateObj.checked && this.map.getZoom() > 9 && updateObj.id !== 'Flood') {
        this.map.setZoom(9);
      }
      if (updateObj.checked && this.map.getZoom() < 14 && updateObj.id === 'Flood') {
        this.map.setZoom(14);
      }
    }
  }

  /**
   * Called whenever the user turns on/off a layer
   * @param  object   updateObj   contains all layers that were configure in config.js
   */
  updateLayerVisibility(updateObj) {
    _.forEach(this.map.layerIds, (id) => {
      if (id.includes('layer') || id.includes(updateObj.id)) {
        return;
      } else {
        this.map.getLayer(id).setVisibility(false);
      }
    });

    let loadInfo = _.find(loads, { label: updateObj.id });

    switch (updateObj.id) {
      case 'Ice':
        this.updateIceVisibility(loadInfo, updateObj.checked);
        break;
      case 'Snow':
        this.updateSnowVisibility(loadInfo, updateObj.checked);
        break;
      case 'Rain':
        this.map.getLayer(updateObj.id).setVisibility(updateObj.checked);
        break;
      case 'Flood':
        this.map.getLayer(updateObj.id).setVisibility(updateObj.checked);
        break;
      case 'Seismic':
        this.updateSeismicVisibility(loadInfo, updateObj.checked);
        break;
      case 'Tsunami':
        this.map.getLayer(updateObj.id).setVisibility(updateObj.checked);
        break;
      case 'Wind':
        this.updateWindVisibility(loadInfo, updateObj.checked);
        break;
      case 'Tornado':
        // TO DO
        break;
      default:
        break;
    }

    if (updateObj.checked && this.map.getZoom() > 9 && updateObj.id !== 'Flood') {
      this.map.setZoom(9);
    }
    if (updateObj.checked && this.map.getZoom() < 14 && updateObj.id === 'Flood') {
      this.map.setZoom(14);
    }

    this.emitChange();
    this.updateLegend();
  }

  hideLegend() {
    this.legend.refresh([]);
  }

  updateLegend() {
    let criteria = criteriaStore.getState();
    const { measurement } = criteria;

    let layerInfos = [];
    let has722WinterWindContourLegend = false;
    _.forEach(this.map.layerIds, (layerId) => {
      if (layerId.includes('layer')) {
        return;
      }

      if (this.map.getLayer(layerId).visible) {
        // create layer info and push into array
        let hideLayers = [];
        _.forEach(this.map.getLayer(layerId).layerInfos, (layerInfo) => {
          if (measurement === 'si') {
            layerInfo.name = layerInfo.name.replace('(Vmphs)', '(Vmps)');
            layerInfo.name = layerInfo.name.replace('(Vmph)', '(Vmps)');
          }

          if (
            layerInfo.name.includes('Wind Contours (Vmph)') ||
            layerInfo.name.includes('i2022_ConcurrentTemp') ||
            layerInfo.name.includes('Wind Speed Guam and Am Samoa')
          ) {
            hideLayers.push(layerInfo.id);
          }

          if (layerInfo.name.includes('Winter Wind Contour')) {
            has722WinterWindContourLegend = true;
          } else {
            // If the name includes contour OR includes labels
            if (
              _.includes(layerInfo.name.toLowerCase(), 'contour') ||
              _.includes(layerInfo.name.toLowerCase(), 'labels') ||
              _.includes(layerInfo.name.toLowerCase(), 'points') ||
              _.includes(layerInfo.name.toLowerCase(), 'wind speed pr')
            ) {
              // If the name includes
              if (
                !_.includes(layerInfo.name, 'Wind Gust Contours') ||
                !_.includes(layerInfo.name, 'Concurrent Temperature Contours') ||
                !_.includes(layerInfo.name, 'Wind Speed (Vmph)')
              ) {
                hideLayers.push(layerInfo.id);
              }
            } else if (layerInfo.name.includes('CONUS ')) {
              layerInfo.name = layerInfo.name.replace('CONUS ', '');
            }
          }
        });

        this.has722WinterWindContourLegend = has722WinterWindContourLegend;

        layerInfos.push({
          layer: this.map.getLayer(layerId),
          hideLayers: hideLayers,
          title: ' ',
        });
      }
    });
    // Refresh the legend with new information
    if (!_.isEmpty(this.legend)) {
      this.legend.refresh(layerInfos);
    }
  }

  updateIceVisibility(loadInfo, checked) {
    let criteria = criteriaStore.getState();
    let layerID = 'Ice_' + criteria.standardsVersion + '_' + criteria.measurement;
    if (criteria.standardsVersion === '7-22') {
      layerID += `_RC_${criteria.riskLevel}`;
    }
    this.map.getLayer(layerID).setVisibility(checked);
  }

  updateSnowVisibility(loadInfo, checked) {
    let criteria = criteriaStore.getState();
    let layerID = `Snow_${criteria.standardsVersion}_${criteria.measurement}`;
    if (criteria.standardsVersion === '7-22') {
      layerID += `_RC_${criteria.riskLevel}`;
    }
    this.map.getLayer(layerID).setVisibility(checked);
  }

  updateSeismicVisibility(loadInfo, checked) {
    let standard = criteriaStore.getState().standardsVersion;
    let layerID = 'Seismic_' + standard;
    this.map.getLayer(layerID).setVisibility(checked);
  }

  updateTornadoVisibility(loadInfo, checked) {
    let criteria = criteriaStore.getState();
    let layerID = `Tornado_${criteria.standardsVersion}_${criteria.measurement}_${criteria.riskLevel}`;
    this.map.getLayer(layerID).setVisibility(checked);
  }

  updateTsunamiVisibility(loadInfo, checked) {
    let criteria = criteriaStore.getState();
    let layerID = criteria.standardsVersion === '7-22' ? 'Tsunami_7-22' : 'Tsunami';
    this.map.getLayer(layerID).setVisibility(checked);
  }

  updateWindVisibility(loadInfo, checked) {
    const { standardsVersion, riskLevel, measurement } = criteriaStore.getState();
    const layerID = `Wind_${standardsVersion}_${riskLevel}_${measurement}`;
    this.map.getLayer(layerID).setVisibility(checked);
    const hurricaneLayer = this.map.getLayer('Wind_Hurricane');
    if (hurricaneLayer) {
      if (checked) {
        hurricaneLayer.setVisibleLayers(standardsVersion === '7-10' ? [0] : [1]);
      }
      hurricaneLayer.setVisibility(checked);
    }
  }

  queryElevation(feature) {
    const lnglat = webMercatorUtils.xyToLngLat(feature.x, feature.y);
    const longitude = parseFloat(lnglat[0].toFixed(6));
    const latitude = parseFloat(lnglat[1].toFixed(6));

    $.get('https://epqs.nationalmap.gov/v1/json', {
      x: longitude,
      y: latitude,
      units: 'Feet',
      output: 'json',
    })
      .then((results) => {
        mapActions.setElevation(results.value);
      })
      .fail(() => {
        mapActions.setElevation(undefined);
      });
  }

  queryElevationReferenceDatum(feature) {
    let lnglat = webMercatorUtils.xyToLngLat(feature.x, feature.y);
    let longitude = parseFloat(lnglat[0].toFixed(6));
    let latitude = parseFloat(lnglat[1].toFixed(6));
    let geo = new Point(longitude, latitude, new SpatialReference({ wkid: 4326 }));

    let identifyReference = new IdentifyTask(elevationReference.layer.queryURL);
    let identifyParameters = new IdentifyParameters();
    identifyParameters.tolerance = 0;
    identifyParameters.SpatialReference = 4326;
    identifyParameters.returnGeometry = true;
    identifyParameters.layerIds = [8];
    identifyParameters.layerOption = IdentifyParameters.LAYER_OPTION_ALL;
    identifyParameters.width = this.map.width;
    identifyParameters.height = this.map.height;
    identifyParameters.mapExtent = this.map.extent;
    identifyParameters.geometry = geo;

    identifyReference.execute(identifyParameters).then((results) => {
      let vdatum = '';
      if (results.length === 0) {
        vdatum = '-1000000';
      } else {
        vdatum = results[0].feature.attributes.zshift !== '0' ? '88' : results[0].feature.attributes.vdatum;
      }
      // let criteria = criteriaStore.getState();
      // if (criteria.standardsVersion === '7-22' && this.isHawaii) {
      //   return;
      // }
      mapActions.setElevationReferenceDatum(vdatum);
    });
  }

  reverseGeocoder(point) {
    let lat = parseFloat(point.lat);
    let long = parseFloat(point.long);

    if (Number.isNaN(lat) || Number.isNaN(long)) {
      return;
    } else {
      let lnglat = webMercatorUtils.lngLatToXY(long, lat);
      let xcoord = parseFloat(lnglat[0].toFixed(6));
      let ycoord = parseFloat(lnglat[1].toFixed(6));
      let geo = new Point(
        xcoord,
        ycoord,
        new SpatialReference({
          wkid: 102100,
        })
      );
      this.mapClickLocation(geo);
    }
  }

  searchAddress(address) {
    this.search.search(address);
  }

  mapClickLocation(point) {
    if (!criteriaStore.getState().snapToAddress) {
      this.noAddressGeoSelection(point);
      return;
    }

    var layerUrl = 'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/reverseGeocode';
    let outSR = JSON.stringify({ wkid: 102100 });
    let location = JSON.stringify(point);
    var layersRequest = esriRequest({
      url: layerUrl,
      content: {
        f: 'json',
        distance: 200,
        outSR: outSR,
        location: location,
      },
      handleAs: 'json',
      callbackParamName: 'callback',
    });

    layersRequest.then(
      (response) => {
        mapActions.searchSelectResult(response);
        this.search.set(
          'value',
          response.address.Address +
            ', ' +
            response.address.City +
            ', ' +
            response.address.Region +
            ', ' +
            response.address.Postal
        );
        this.queryElevation(response.location);
        this.queryElevationReferenceDatum(response.location);
        this.map.graphics.clear();
        let mapPoint = new Point(response.location);

        let pictureMarkerSymbol = new PictureMarkerSymbol(
          'https://js.arcgis.com/3.36/esri/dijit/Search/images/search-pointer.png',
          36,
          36
        );
        pictureMarkerSymbol.setOffset(9, 18);
        let graphic = new Graphic(mapPoint, pictureMarkerSymbol);
        this.map.graphics.add(graphic);

        // Update the criter store location state
        const object = {
          address: {
            Address: response.address.Address,
            City: response.address.City,
            Region: response.address.Region,
            Postal: response.address.Postal,
          },
          location: {
            x: response.location.x,
            y: response.location.y,
          },
        };
        mapActions.searchSelectResult(object);

        if (this.map.getZoom() > 16) {
          this.map.zoom(mapPoint);
        } else {
          this.map.centerAndZoom(mapPoint, 16);
        }
      },
      () => {
        // no addresses available
        this.noAddressGeoSelection(point);
      }
    );
  }

  noAddressGeoSelection(point) {
    this.search.clear();
    this.map.graphics.clear();

    let response = {};
    response.address = {
      Address: '',
      City: '',
      Region: '',
      Postal: '',
    };
    response.location = point;

    mapActions.searchSelectResult.defer(response);

    this.queryElevation(point);
    this.queryElevationReferenceDatum(point);

    let mapPoint = new Point(point);
    let pictureMarkerSymbol = new PictureMarkerSymbol(
      'https://js.arcgis.com/3.16/esri/dijit/Search/images/search-pointer.png',
      36,
      36
    );
    pictureMarkerSymbol.setOffset(9, 18);
    let graphic = new Graphic(mapPoint, pictureMarkerSymbol);
    this.map.graphics.add(graphic);

    if (this.map.getZoom() > 16) {
      this.map.centerAndZoom(mapPoint, this.map.getZoom());
    } else {
      this.map.centerAndZoom(mapPoint, 16);
    }
  }

  selectionStateChange(state) {
    this.selectionState = state;
  }

  goToSite() {
    const latitude = criteriaStore.getState().latitude;
    const longitude = criteriaStore.getState().longitude;
    let point = null;

    if (latitude === 'Latitude' && longitude === 'Longitude') {
      point = new Point(-96.1951167, 40.35397);
      this.map.centerAndZoom(point, 5);
    } else {
      point = new Point(longitude, latitude);
      this.map.centerAt(point);
    }
  }

  updateVisibility(visibilityObject) {
    // visibilityObject.id - LOAD LABEL
    // visibilityObject.checked - BOOLEAN of SWITCH

    // Accesses report store and change loads state
    let loads = reportStore.getState().loads;
    loads.map((load) => {
      if (load.label === visibilityObject.id) {
        load.visible = visibilityObject.checked;
      } else {
        load.visible = false;
      }
    });

    // Tells reportStore state has changed
    reportStore.emitChange();

    // Now that loads state has changed, change the map accordingly
    this.map.layerIds.forEach((id) => {
      // If the layer ID is the basemap OR the matching layer - do nothing
      if (id.includes('layer') || id.includes(visibilityObject.id)) {
        return;
      } else {
        // Turn off all other layers
        this.map.getLayer(id).setVisibility(false);
      }
    });

    // Current load whose visibility is being changed
    let currentLoad = _.find(loads, { label: visibilityObject.id });
    // Update the visibility accordingly based on type of load
    switch (visibilityObject.id) {
      case 'Ice':
        this.updateIceVisibility(currentLoad, visibilityObject.checked);
        break;
      case 'Snow':
        this.updateSnowVisibility(currentLoad, visibilityObject.checked);
        break;
      case 'Seismic':
        this.updateSeismicVisibility(currentLoad, visibilityObject.checked);
        break;
      case 'Wind':
        this.updateWindVisibility(currentLoad, visibilityObject.checked);
        break;
      case 'Tornado':
        this.updateTornadoVisibility(currentLoad, visibilityObject.checked);
        break;
      case 'Tsunami':
        this.updateTsunamiVisibility(currentLoad, visibilityObject.checked);
        break;
      case 'Rain':
      case 'Flood':
        this.map.getLayer(visibilityObject.id).setVisibility(visibilityObject.checked);
        break;
    }

    // Change the map zoom accordingly if the layer has been turned on
    if (visibilityObject.checked) {
      if (visibilityObject.id === 'Flood') {
        this.map.setZoom(15);
      } else {
        this.map.setZoom(9);
      }
    }

    // Update the legend accordingly
    this.updateLegend();
  }
}

export const mapStore = alt.createStore(MapStore, 'MapStore');
